import type { XYPosition } from "@xyflow/react";

import { RULE_CANVAS_GRID_SIZE } from "../canvas/constants";
import type { RuleEdge } from "../types/edges";
import type { ActionRuleNode, AssignmentRuleNode, ClearReactionRuleNode, DecisionRuleNode, EventListenerRuleNode, FilterRuleNode, GhostRuleNode, LoopRuleNode, QueryRuleNode, ReactionLocationRuleNode, ReactionRuleNode, RuleNode, RuleNodeType, SortRuleNode, StopRuleNode } from "../types/nodes";

/** Collects non-empty `data.name` values from all nodes (global uniqueness for default labels). */
export const collectTakenDisplayNames = (nodes: RuleNode[]): Set<string> => {
	const taken = new Set<string>();
	for (const n of nodes) {
		const name = n.data?.name;
		if (typeof name === "string" && name.trim() !== "") taken.add(name);
	}
	return taken;
};

/**
 * Recursively collects the trimmed names of every loop node across the entire
 * node tree (main canvas + all nested sub-canvases). Use this to enforce
 * globally-unique loop names regardless of canvas depth.
 */
export const collectAllLoopNames = (nodes: RuleNode[]): Set<string> => {
	const names = new Set<string>();
	const visit = (nodeList: RuleNode[]) => {
		for (const n of nodeList) {
			if (n.type === "loop") {
				const name = n.data?.name;
				if (typeof name === "string" && name.trim() !== "") names.add(name.trim().toLowerCase());
				const inner = (n.data as { loopNodes?: RuleNode[] }).loopNodes;
				if (Array.isArray(inner) && inner.length > 0) visit(inner);
			}
		}
	};
	visit(nodes);
	return names;
};

const RAS_TYPES = new Set<RuleNodeType>(["reaction", "assignment", "decision"]);

/**
 * Recursively collects trimmed lowercase names for reaction, assignment, and decision nodes
 * across the full rule graph (main canvas + nested loop bodies). Used like {@link collectAllLoopNames}
 * so these node types cannot share a display name at any depth.
 */
export const collectAllReactionAssignmentDecisionNames = (nodes: RuleNode[]): Set<string> => {
	const names = new Set<string>();
	const visit = (nodeList: RuleNode[]) => {
		for (const n of nodeList) {
			if (RAS_TYPES.has(n.type)) {
				const name = n.data?.name;
				if (typeof name === "string" && name.trim() !== "") names.add(name.trim().toLowerCase());
			}
			if (n.type === "loop") {
				const inner = (n.data as { loopNodes?: RuleNode[] }).loopNodes;
				if (Array.isArray(inner) && inner.length > 0) visit(inner);
			}
		}
	};
	visit(nodes);
	return names;
};

/** Depth-first search including nested `loop.loopNodes`. */
export const findRuleNodeByIdInTree = (nodeList: RuleNode[], id: string): RuleNode | undefined => {
	for (const n of nodeList) {
		if (n.id === id) return n;
		if (n.type === "loop") {
			const inner = (n.data as { loopNodes?: RuleNode[] }).loopNodes;
			if (Array.isArray(inner) && inner.length > 0) {
				const found = findRuleNodeByIdInTree(inner, id);
				if (found) return found;
			}
		}
	}
	return undefined;
};

/**
 * Picks the next unused display name: `base`, then `base 1`, `base 2`, …
 * so new nodes never duplicate an existing name on the canvas.
 * Pass `caseInsensitive: true` when the `taken` set stores lowercase keys
 * (e.g. the global loop-names set).
 */
export const getUniqueDefaultName = (
	baseName: string,
	taken: Set<string>,
	caseInsensitive = false,
): string => {
	const key = (s: string) => (caseInsensitive ? s.toLowerCase() : s);
	if (!taken.has(key(baseName))) return baseName;
	let n = 1;
	while (taken.has(key(`${baseName} ${n}`))) n += 1;
	return `${baseName} ${n}`;
};

export const snapPositionToGrid = (position: XYPosition): XYPosition => ({
	x: Math.round(position.x / RULE_CANVAS_GRID_SIZE) * RULE_CANVAS_GRID_SIZE,
	y: Math.round(position.y / RULE_CANVAS_GRID_SIZE) * RULE_CANVAS_GRID_SIZE,
});

export const alignNodesToGrid = (nodes: RuleNode[]): RuleNode[] =>
	nodes.map((node) => ({
		...node,
		position: snapPositionToGrid(node.position),
	}));

export const DEFAULT_NODE_OFFSET_Y = RULE_CANVAS_GRID_SIZE * 5;
export const DEFAULT_NODE_POSITION = snapPositionToGrid({ x: RULE_CANVAS_GRID_SIZE * 6, y: RULE_CANVAS_GRID_SIZE * 6 });

export const buildNodeId = (type: RuleNodeType) => `${type}-${crypto.randomUUID?.() ?? Math.random().toString(16).slice(2)}`;

export const buildDecisionNode = (position: XYPosition, id?: string, displayName?: string): DecisionRuleNode => {
	const name = displayName ?? "Decision";
	return {
		id: id ?? buildNodeId("decision"),
		type: "decision",
		position: snapPositionToGrid(position),
		data: {
			type: "decision",
			name,
		},
	};
};

export const buildLoopNode = (position: XYPosition, id?: string, displayName?: string): LoopRuleNode => {
	const name = displayName ?? "Loop";
	return {
		id: id ?? buildNodeId("loop"),
		type: "loop",
		position: snapPositionToGrid(position),
		data: {
			type: "loop",
			label: name,
			name,
			loopType: "variable",
		},
	};
};

export const buildSortNode = (position: XYPosition, id?: string, displayName?: string): SortRuleNode => {
	const name = displayName ?? "Sort";
	return {
		id: id ?? buildNodeId("sort"),
		type: "sort",
		position: snapPositionToGrid(position),
		data: {
			type: "sort",
			label: name,
			name,
		},
	};
};

export const buildFilterNode = (position: XYPosition, id?: string, displayName?: string): FilterRuleNode => {
	const name = displayName ?? "Filter";
	return {
		id: id ?? buildNodeId("filter"),
		type: "filter",
		position: snapPositionToGrid(position),
		data: {
			type: "filter",
			label: name,
			name,
		},
	};
};

export const buildQueryNode = (position: XYPosition, id?: string, displayName?: string): QueryRuleNode => {
	const name = displayName ?? "Query Builder";
	return {
		id: id ?? buildNodeId("query_builder"),
		type: "query_builder",
		position: snapPositionToGrid(position),
		data: {
			type: "query_builder",
			label: name,
			name,
			table: "",
		},
	};
};

export const buildReactionNode = (
	position: XYPosition,
	id?: string,
	displayName?: string,
	reactionTypeDefaults?: {
		reactionTypeId: string;
		reactionTypeName: string;
		reactionTypeFriendlyName?: string;
	},
): ReactionRuleNode => {
	const name = displayName ?? "Reaction";
	return {
		id: id ?? buildNodeId("reaction"),
		type: "reaction",
		position: snapPositionToGrid(position),
		data: {
			type: "reaction",
			label: name,
			name,
			reactionTypeId: reactionTypeDefaults?.reactionTypeId ?? "",
			reactionTypeName: reactionTypeDefaults?.reactionTypeName ?? "",
			reactionTypeFriendlyName: reactionTypeDefaults?.reactionTypeFriendlyName ?? "",
		},
	};
};

export const buildClearReactionNode = (position: XYPosition, id?: string, displayName?: string): ClearReactionRuleNode => {
	const name = displayName ?? "Clear Reaction";
	return {
		id: id ?? buildNodeId("clear_reaction"),
		type: "clear_reaction",
		position: snapPositionToGrid(position),
		data: {
			type: "clear_reaction",
			label: name,
			name,
		},
	};
};

export const buildStopNode = (position: XYPosition, id?: string, displayName?: string): StopRuleNode => {
	const name = displayName ?? "Stop";
	return {
		id: id ?? buildNodeId("stop"),
		type: "stop",
		position: snapPositionToGrid(position),
		data: {
			type: "stop",
			label: name,
			name,
		},
	};
};

export const buildEventListenerNode = (
	position: XYPosition,
	id?: string,
	displayName?: string,
): EventListenerRuleNode => {
	const name = displayName ?? "Event Listener";
	return {
		id: id ?? buildNodeId("event_listener"),
		type: "event_listener",
		position: snapPositionToGrid(position),
		data: {
			type: "event_listener",
			label: name,
			name,
			targetScreenFieldIds: [],
			action: "click",
		},
	};
};

export const buildActionNode = (
	position: XYPosition,
	id?: string,
	displayName?: string,
): ActionRuleNode => {
	const name = displayName ?? "Action";
	return {
		id: id ?? buildNodeId("action"),
		type: "action",
		position: snapPositionToGrid(position),
		data: {
			type: "action",
			label: name,
			name,
			actionType: "click",
		},
	};
};

export const buildAssignmentNode = (position: XYPosition, id?: string, displayName?: string): AssignmentRuleNode => {
	const name = displayName ?? "Assignment";
	return {
		id: id ?? buildNodeId("assignment"),
		type: "assignment",
		position: snapPositionToGrid(position),
		data: {
			type: "assignment",
			label: name,
			name,
		},
	};
};

export const buildInitialReactionNode = (
	ruleName: string,
	screenLocation: string,
	previewUrl?: string | null,
): ReactionLocationRuleNode => {
	const normalizedPreview =
		previewUrl != null && typeof previewUrl === "string" && previewUrl.trim() !== ""
			? previewUrl.trim().replace(/\r/g, "")
			: undefined;

	return {
		id: buildNodeId("reaction_location"),
		type: "reaction_location",
		position: snapPositionToGrid({ x: 250, y: 150 }),
		data: {
			type: "reaction_location",
			label: "Reaction Location",
			name: ruleName,
			screenLocation,
			...(normalizedPreview != null ? { previewUrl: normalizedPreview } : {}),
		},
	};
};

export const buildGhostNode = (position: XYPosition, id?: string): GhostRuleNode => ({
	id: id ?? buildNodeId("ghost"),
	type: "ghost",
	position: snapPositionToGrid(position),
	data: {
		type: "ghost",
		label: "Ghost",
		name: "Ghost",
	},
});

/**
 * Deletes a node from the graph while preserving any decision-true / decision-false
 * edges that point TO it.  Those edges are redirected to fresh ghost nodes placed at
 * the deleted node's position so the decision paths remain visible, mirroring the
 * behaviour of dropping a reconnect on empty canvas.
 *
 * All other edges connected to the deleted node (as source or target) are removed.
 * 
 * TODO:: The conditional logic here can be extended for other edge types in the future, likely will need to do the same for loop-start and loop-end edges.
 */
export const deleteNodePreservingDecisionEdges = (
	nodeId: string,
	nodes: RuleNode[],
	edges: RuleEdge[],
): { nodes: RuleNode[]; edges: RuleEdge[] } => {
	const targetNode = nodes.find((n) => n.id === nodeId);
	if (!targetNode) return { nodes, edges };

	// Get the decision edges that point TO the deleted node, we want to preserve these
	const preservedDecisionEdges = edges.filter(
		(e) =>
			e.target === nodeId &&
			(e.type === "decision-true" || e.type === "decision-false"),
	);

	const preservedDecisionEdgeIds = new Set(preservedDecisionEdges.map((e) => e.id));

	const edgeIdsToRemove = new Set(
		edges
			.filter((e) => e.source === nodeId || e.target === nodeId)
			.filter((e) => !preservedDecisionEdgeIds.has(e.id))
			.map((e) => e.id),
	);

	// Build one ghost node per preserved decision edge and place it at the top-center of where the deleted node was.
	const deletedNodeCenter = {
		x: targetNode.position.x + 125,
		y: targetNode.position.y,
	};
	const ghostsForEdges = preservedDecisionEdges.map((e) => ({
		edge: e,
		ghost: buildGhostNode(deletedNodeCenter),
	}));

	const updatedEdges = edges
		.filter((e) => !edgeIdsToRemove.has(e.id))
		.map((e) => {
			const match = ghostsForEdges.find((g) => g.edge.id === e.id);
			if (match) return { ...e, target: match.ghost.id, targetHandle: null };
			return e;
		});

	const updatedNodes = [
		...nodes.filter((n) => n.id !== nodeId),
		...ghostsForEdges.map((g) => g.ghost),
	];

	return { nodes: updatedNodes, edges: updatedEdges };
};